import { useState } from "react";
import { Button, IconButton, Tooltip } from "@mui/material";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import FolderCopyRoundedIcon from "@mui/icons-material/FolderCopyRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import DatagridCustom from "../../General/DatagridCustom";
import AppDialog from "../../General/Dialog/AppDialog";

function formatDateTime(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const operationColumns = [
  { field: "number_operation", headerName: "N° Operación", flex: 1 },
  {
    field: "date",
    headerName: "Fecha",
    flex: 1,
    renderCell: (params) => formatDateTime(params.value),
  },
  { field: "type", headerName: "Tipo", flex: 2 },
  { field: "status", headerName: "Estado", flex: 1 },
];

export default function TaskLegalFilesTable({
  rows,
  loading,
  onExport,
  exporting,
  canExport,
}) {
  const [selectedLegalFile, setSelectedLegalFile] = useState(null);

  const columns = [
    {
      field: "code",
      headerName: "Código de expediente",
      flex: 1.5,
      renderCell: (params) => {
        const legalFileId = params.row.id;
        const code = params.value || "—";

        if (!legalFileId) return code;

        return (
          <Tooltip title="Ver detalle del expediente" placement="top">
            <Button
              component="a"
              href={`/legal/file/${legalFileId}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              size="small"
              sx={{
                minWidth: "auto",
                p: 0,
                fontWeight: 900,
                textTransform: "none",
                justifyContent: "flex-start",
                color: "#7d7d7d",
              }}
            >
              {code}
            </Button>
          </Tooltip>
        );
      },
    },
    {
      field: "main_defendant",
      headerName: "Demandado principal",
      flex: 1.5,
      valueGetter: (params, row) => row?.main_defendant || "—",
    },
    {
      field: "system_state",
      headerName: "Estado del sistema",
      flex: 1.3,
      valueGetter: (params, row) => row?.system_state?.name || "—",
    },
    {
      field: "lawyer",
      headerName: "Abogado",
      flex: 1.5,
      valueGetter: (params, row) => {
        const lawyer = row?.lawyer;
        return lawyer ? `${lawyer.name} ${lawyer.lastname}` : "—";
      },
    },
    {
      field: "operations",
      headerName: "Operaciones",
      flex: 1,
      valueGetter: (params, row) => (row?.operations || []).length,
    },
    {
      field: "actions",
      headerName: "Detalle",
      sortable: false,
      flex: 0.7,
      renderCell: (params) => (
        <IconButton
          color="primary"
          onClick={() => setSelectedLegalFile(params.row)}
          aria-label="ver operaciones"
        >
          <ListAltRoundedIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <DatagridCustom
        rows={rows}
        columns={columns}
        title="Expedientes de la tarea"
        description="Resultado dinámico según los filtros aplicados."
        icon={<FolderCopyRoundedIcon />}
        loading={loading}
        getRowId={(row) => row.id ?? row.code}
        hideExport
        headerActions={
          canExport ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadRoundedIcon />}
              onClick={onExport}
              disabled={exporting}
              sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none" }}
            >
              {exporting ? "Generando..." : "Exportar a Excel"}
            </Button>
          ) : null
        }
      />

      <AppDialog
        open={!!selectedLegalFile}
        onClose={() => setSelectedLegalFile(null)}
        maxWidth="md"
        title={`Operaciones — ${selectedLegalFile?.code ?? ""}`}
        icon={<ListAltRoundedIcon />}
      >
        <DatagridCustom
          rows={selectedLegalFile?.operations || []}
          columns={operationColumns}
          getRowId={(row) => row.id ?? row.number_operation}
        />
      </AppDialog>
    </>
  );
}
